import React, { lazy, Suspense, useRef, useState } from 'react';
import getConfig from 'next/config';
import { gql, useQuery } from '@apollo/client';
import Header, { Breadcrumbs } from '@domain-group/fe-co-header';
import {
  ContactCard2026,
  ContactCardVariant,
  ContactProfileFooterWithFragment,
} from '..';
import {
  ProfilePlaceholder as Placeholder,
  StatTiles,
  StatTilesVariant,
} from '../shared';
import getStatTilesVariant from './get-stat-tiles-variant';
import AgentProfileAgencyBanner from '../agent-profile-agency-banner/agent-profile-agency-banner';
import AgentProfileOverview from '../agent-profile-overview/agent-profile-overview';
import AgentProfileProperties2026 from './agent-profile-properties-2026';
import AgentProfilePhoto from '../agent-profile-photo/agent-profile-photo';
import AgentProfileReviews from '../agent-profile-reviews/agent-profile-reviews';
import { trackContactRecommendations } from '../../tracking';
import { MOBILE_BANNER_HEIGHT } from '../agent-profile-agency-banner/agent-profile-agency-banner.style';
import usePhotoCovered from './use-photo-covered';
import { mapContactBreadcrumbs, mapHeaderData } from '../header/utils';
import { skipLinkName } from '../../helper/accessibility-utils';
import { GetAgentProfile2026Query } from '../../generated/graphql';
import { COMPONENT_IDS } from '../../constants';
import buildAgencyProfileUrl from '../../utils/build-agency-profile-url';
import * as styles from './agent-profile-2026.style';

const { publicRuntimeConfig } = getConfig();

// Lazy-loaded so Formik/Yup and the illustration package it pulls in aren't
// in every profile page visitor's initial bundle - only fetched once someone
// actually opens the enquiry form.
const EnquiryModal2026 = lazy(() => import('../enquiry-modal-2026'));

/* eslint-disable react/no-unused-prop-types -- not consumed on this skeleton yet, plugged in as later sections land */
export interface AgentProfile2026Props {
  contactId: number;
  requestAppraisalUrl: string;
  mixpanelHasInitialized: boolean;
  isEmbeddedApp?: boolean;
  starRatingsEnabled?: boolean;
  hideStarRatings?: boolean;
  hideAppraisalCta?: boolean;
  imageFormat?: string;
  user?: { userId?: string };
  searchSuburbSlug?: string;
}
/* eslint-enable react/no-unused-prop-types */

// This query is the 2026 profile's own data contract, growing to cover each
// section as it lands (currently breadcrumbs/footer identifiers, the identity
// block, and the Section_Overview stat tiles). The header here is always a
// plain, solid Header (unlike the
// legacy ContactProfileHeaderWithFragment, which goes transparent for
// platinum agents to sit over the Hero photo - there's no photo behind it
// yet on this skeleton).
export const AGENT_PROFILE_2026_QUERY = gql`
  query getAgentProfile2026($contactId: Int!, $imageFormat: ImageFormat) {
    contactByContactId(contactId: $contactId) {
      id
      agentIdV2
      agencyId
      name
      firstName
      agencyName
      agencyUrlPath
      jobCategory
      jobTitle
      hasReinsw
      hasEmail
      telephone
      mobile
      profilePhoto {
        url(imageFormat: $imageFormat)
      }
      profileUrl
      profileTier
      depthTier
      recommendations {
        numberOfReviews
        overallStarRating
      }
      agencyByAgencyId {
        id
        name
        profileUrl
        address {
          displayAddress
          suburb {
            id
            suburbId
            name
            slug
          }
        }
        branding {
          id
          backgroundColour
          logo {
            url(imageFormat: $imageFormat)
          }
        }
      }
      address {
        displayAddress
        suburb {
          id
          suburbId
          name
          slug
        }
      }
      statistics {
        totalForSale
        totalSoldAndAuctioned
        totalForRent
        totalLeased
        averageDaysOnMarket
        averageSoldPrice
      }
    }
  }
`;

const STATS_CAVEAT = 'Based on data from the past 12 months';

const CONTACT_CARD_VARIANT_BY_STAT_TILES_VARIANT: Record<
  StatTilesVariant,
  ContactCardVariant
> = {
  sales: 'appraisal',
  rental: 'propertyManager',
  administrator: 'administrator',
};

const AgentProfile2026 = ({
  contactId,
  requestAppraisalUrl,
  isEmbeddedApp = false,
  starRatingsEnabled = false,
  hideStarRatings = false,
  hideAppraisalCta = false,
  imageFormat,
  searchSuburbSlug,
}: AgentProfile2026Props): JSX.Element => {
  const { data, loading, error } = useQuery<GetAgentProfile2026Query>(
    AGENT_PROFILE_2026_QUERY,
    { variables: { contactId, imageFormat } },
  );
  const [isPhotoCovered, sheetTopRef] =
    usePhotoCovered<HTMLDivElement>(MOBILE_BANNER_HEIGHT);
  const contact = data?.contactByContactId;
  const { agentIdV2, agencyId } = contact || {};
  const heroAvatarRef = useRef<HTMLDivElement>(null);
  const heroAvatarRefs = useRef([heroAvatarRef]).current;
  const agency = contact?.agencyByAgencyId;
  const [isEnquiryModalOpen, setIsEnquiryModalOpen] = useState(false);

  if (error) {
    throw new Error(`Something went wrong, ${error}`);
  }

  if (!loading && (!data || !agentIdV2 || !agencyId)) {
    throw new Error('Insufficient data to render Agent Profile page.');
  }

  const breadcrumbs = mapContactBreadcrumbs({
    baseUrl: publicRuntimeConfig.BASE_URL,
    profileUrl: contact?.profileUrl?.split('/').filter(Boolean).pop(),
    name: contact?.name ?? undefined,
    suburbName: contact?.address?.suburb?.name ?? undefined,
    suburbSlug: contact?.address?.suburb?.slug ?? undefined,
    statistics: contact?.statistics,
  });

  const agencyUrl = buildAgencyProfileUrl(
    agency?.profileUrl ?? contact?.agencyUrlPath,
    agencyId,
  );
  const bannerProps =
    contact && agencyUrl
      ? {
          agencyName: agency?.name ?? contact.agencyName ?? '',
          agencyUrl,
          address:
            agency?.address?.displayAddress ??
            contact.address?.displayAddress ??
            '',
          suburb:
            agency?.address?.suburb?.name ??
            contact.address?.suburb?.name ??
            '',
          logoUrl: agency?.branding?.logo?.url,
          brandColour: agency?.branding?.backgroundColour,
          agentName: contact.name ?? undefined,
        }
      : null;

  const agencySuburbSource =
    agency?.address?.suburb?.name && agency?.address?.suburb?.slug
      ? agency.address.suburb
      : contact?.address?.suburb;
  const agencySuburb =
    agencySuburbSource?.name && agencySuburbSource?.slug
      ? { name: agencySuburbSource.name, slug: agencySuburbSource.slug }
      : null;

  const totalReviews = contact?.recommendations?.numberOfReviews;
  const overallStarRating = contact?.recommendations?.overallStarRating;
  const shouldShowRating =
    starRatingsEnabled &&
    !hideStarRatings &&
    !!overallStarRating &&
    !!totalReviews;
  const overviewProps = contact
    ? {
        agentName: contact.name ?? '',
        jobTitle: contact.jobTitle,
        jobCategory: contact.jobCategory,
        agencyName: contact.agencyName,
        agencyUrl: buildAgencyProfileUrl(
          contact.agencyUrlPath,
          contact.agencyId,
        ),
        overallStarRating: shouldShowRating ? overallStarRating : undefined,
        totalReviews: shouldShowRating ? totalReviews : undefined,
        isReinswMember: !!contact.hasReinsw,
      }
    : null;

  return (
    <div>
      {!isEmbeddedApp && (
        <>
          <Header
            {...mapHeaderData({
              baseUrl: publicRuntimeConfig.BASE_URL,
              breadcrumbsData: null,
            })}
          />
          <div css={styles.breadcrumbBar}>
            <div css={[styles.wrapper, styles.breadcrumbs]}>
              <Breadcrumbs
                baseUrl={publicRuntimeConfig.BASE_URL}
                breadcrumbs={breadcrumbs}
                backButtonLabel="Back"
              />
            </div>
          </div>
        </>
      )}
      <main id={skipLinkName} data-testid="trade-profile-2026">
        <div css={styles.mainContainer}>
          {bannerProps && (
            <div
              css={styles.bannerSlot}
              data-testid="agent-profile-2026__banner"
            >
              <AgentProfileAgencyBanner
                {...bannerProps}
                showAgentName={isPhotoCovered}
              />
            </div>
          )}
          <div
            css={styles.bodyContainer}
            data-testid="agent-profile-2026__hero"
          >
            {overviewProps && (
              <div
                css={styles.details}
                data-testid="agent-profile-2026__details"
              >
                <div ref={sheetTopRef} />
                <AgentProfileOverview {...overviewProps} />
              </div>
            )}
            <div css={styles.tiles} data-testid="agent-profile-2026__tiles">
              <StatTiles
                variant={getStatTilesVariant(contact?.jobCategory)}
                statistics={contact?.statistics ?? {}}
                caveat={STATS_CAVEAT}
              />
            </div>
            <div css={styles.content} data-testid="agent-profile-2026__content">
              <div css={styles.divider} />
              <AgentProfileProperties2026 contactId={contactId} />
              <Placeholder label="Section_MyTrackRecord" minHeight={714} />
              <Placeholder label="Section_About" minHeight={779} />
              <div id={COMPONENT_IDS.recommendations}>
                {contact && (
                  <AgentProfileReviews
                    key={contactId}
                    contactId={contactId}
                    agentFirstName={contact.firstName ?? ''}
                    profileTier={contact.profileTier}
                    depthTier={contact.depthTier}
                    overallStarRating={
                      contact.recommendations?.overallStarRating
                    }
                    totalReviews={contact.recommendations?.numberOfReviews}
                    showStarRating={shouldShowRating}
                    onPageRendered={
                      trackContactRecommendations.trackContactPageRendered
                    }
                    onVendorRecoViewAll={
                      trackContactRecommendations.trackContactViewAll
                    }
                    onVendorRecoShowMore={
                      trackContactRecommendations.trackContactShowMore
                    }
                  />
                )}
              </div>
            </div>
            <div css={styles.sidebar} data-testid="agent-profile-2026__sidebar">
              <div
                css={styles.photo}
                data-testid="agent-profile-2026__photo"
                ref={heroAvatarRef}
              >
                <AgentProfilePhoto
                  name={contact?.name ?? ''}
                  photoUrl={contact?.profilePhoto?.url}
                />
              </div>
              {contact && (
                <ContactCard2026
                  variant={
                    CONTACT_CARD_VARIANT_BY_STAT_TILES_VARIANT[
                      getStatTilesVariant(contact.jobCategory)
                    ]
                  }
                  name={contact.name ?? ''}
                  jobTitle={contact.jobTitle || contact.jobCategory}
                  profilePhoto={contact.profilePhoto?.url}
                  telephone={contact.mobile || contact.telephone}
                  hasEmail={contact.hasEmail}
                  hideAppraisalCta={hideAppraisalCta}
                  requestAppraisalUrl={requestAppraisalUrl}
                  contactId={contactId}
                  heroAvatarRefs={heroAvatarRefs}
                  onOpenEnquiryForm={() => setIsEnquiryModalOpen(true)}
                />
              )}
            </div>
          </div>
        </div>
      </main>
      {contact && isEnquiryModalOpen && (
        <Suspense fallback={null}>
          <EnquiryModal2026
            contactId={contactId}
            agentName={contact.name ?? ''}
            jobTitle={contact.jobTitle || contact.jobCategory}
            agencyName={agency?.name ?? contact.agencyName}
            profilePhoto={contact.profilePhoto?.url}
            telephone={contact.mobile || contact.telephone}
            agencySuburb={agencySuburb}
            searchSuburbSlug={searchSuburbSlug}
            onClose={() => setIsEnquiryModalOpen(false)}
          />
        </Suspense>
      )}
      {!isEmbeddedApp && agentIdV2 && agencyId && (
        <ContactProfileFooterWithFragment
          agentIdV2={agentIdV2}
          agencyId={agencyId}
        />
      )}
    </div>
  );
};

export default AgentProfile2026;
